import { Runtime, analytics, useSiteInfo } from '@silvertours/front-entities';
import { useEffect, useRef, useState } from 'react';

const EXPERIMENT_ID = 'app_install_banner_test';

/**
 * Time to wait for the Mixpanel assignment. After this time the banner shows
 * as before, and a late assignment has no effect on this page.
 */
export const ASSIGNMENT_TIMEOUT_MS = 2000;

type Variant = 'control' | 'hide_banner';

/**
 * A/B test for the app install banner (LPOT-1734). Mixpanel assigns the
 * variant and pushes the `hideAppInstallBanner` toggle for both arms. Without
 * Mixpanel consent there is no assignment and the banner shows as before.
 *
 * @param isEligible `true` when the banner can show on this page.
 * @param isBannerShown `true` when the banner shows or showed on this page.
 */
export const useAppBannerExperiment = (
  isEligible: boolean,
  isBannerShown: boolean,
) => {
  const { dynamicFeatureToggles } = useSiteInfo();
  const hasMixpanelConsent = Runtime.useConsent(
    Runtime.Services.mixpanelProfiles,
  );
  const [isTimedOut, setIsTimedOut] = useState(false);
  const isExposureTracked = useRef(false);

  const assignment = dynamicFeatureToggles.hideAppInstallBanner;
  const isAwaitingAssignment =
    isEligible && hasMixpanelConsent && assignment === undefined;

  useEffect(() => {
    if (!isAwaitingAssignment || isTimedOut) {
      return undefined;
    }

    const timer = setTimeout(() => setIsTimedOut(true), ASSIGNMENT_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isAwaitingAssignment, isTimedOut]);

  let variant: Variant | undefined;
  if (
    isEligible &&
    hasMixpanelConsent &&
    !isTimedOut &&
    assignment !== undefined
  ) {
    variant = assignment ? 'hide_banner' : 'control';
  }

  // The user saw the banner before the assignment arrived, so the page cannot
  // use the hide_banner variant. This late assignment has no effect.
  if (isBannerShown && variant === 'hide_banner') {
    variant = undefined;
  }

  useEffect(() => {
    if (variant && !isExposureTracked.current) {
      isExposureTracked.current = true;
      analytics.generic.trackExperimentViewed(EXPERIMENT_ID, variant);
    }
  }, [variant]);

  return {
    isPending: isAwaitingAssignment && !isTimedOut,
    isBannerHidden: variant === 'hide_banner',
  };
};
